import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderKpiCard, FolderStayCard } from '@koob/booking';
import { buildFolderSummary } from '@koob/utils';
import { formatDate, formatDateTime } from '@koob/dates';
import { formatCurrency } from 'utils';

/**
 * KD-3842 TO summary of a client folder: the stay and four KPIs (total price,
 * bookings, travelers, last update), assembled from the shared bricks.
 */
export default function FoldersSummary({ folder }) {
  const { t } = useTranslation('folders');
  const summary = buildFolderSummary(folder);
  const folderCurrency =
    folder?.currency ??
    folder?.trips?.find(trip => trip.draftPrice?.currency)?.draftPrice
      ?.currency;
  const { totals, bookings, mainGuest } = summary;
  // KD-3903: one subtotal per currency, never converted. Every amount shows
  // its currency, the price summary lines included (KD-3949).
  const money = (amount, currency) =>
    formatCurrency({ amount, currency: currency ?? folderCurrency });
  // KD-3949: the price summary as production shows it, one line per part,
  // services before adjustments and the adjustments apart, never cut.
  const priceParts = ({ priceSummary: amounts }) =>
    [
      { part: 'hotels', amount: amounts.hotels },
      { part: 'experiences', amount: amounts.experiences },
      amounts.draftTrips
        ? { part: 'draftTrips', amount: amounts.draftTrips }
        : null,
      amounts.adjustments
        ? { part: 'adjustments', amount: amounts.adjustments }
        : null,
    ].filter(Boolean);
  const partColor = ({ part, amount }) => {
    if (part !== 'adjustments') return 'text-gray-700';
    return amount >= 0 ? 'text-emerald-600' : 'text-rose-600';
  };
  const priceSummary = summary.hasServices ? (
    <dl
      className="m-0 mt-1 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 text-xs"
      data-cy="folder-price-summary"
    >
      {totals.flatMap(total =>
        priceParts(total).map(item => (
          <Fragment key={`${total.currency ?? ''}-${item.part}`}>
            <dt className="truncate text-gray-500">
              {t(`summary.priceSummary.${item.part}`)}
            </dt>
            <dd
              className={`m-0 whitespace-nowrap text-right tabular-nums ${partColor(item)}`}
            >
              {money(item.amount, total.currency)}
            </dd>
          </Fragment>
        )),
      )}
    </dl>
  ) : null;
  let totalValue = '--';
  if (summary.hasServices) {
    totalValue =
      totals.length > 0
        ? totals.map(({ currency, total }) => (
            <span key={currency ?? ''} className="block truncate">
              {money(total, currency)}
            </span>
          ))
        : money(0);
  }

  const author = folder?.createdBy?.firstName
    ? `${folder.createdBy.firstName} ${folder.createdBy.lastName ?? ''}`.trim()
    : '--';
  const mainGuestName = mainGuest
    ? `${mainGuest.firstName ?? ''} ${mainGuest.lastName ?? ''}`.trim()
    : '';

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <FolderStayCard
          startAt={summary.stay.startAt}
          endAt={summary.stay.endAt}
          nights={summary.stay.nights}
          destinations={summary.stay.destinations}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FolderKpiCard
            label={t('summary.totalPrice')}
            value={totalValue}
            sub={summary.hasServices ? null : t('summary.noRevenue')}
            details={priceSummary}
            highlight={summary.hasServices}
          />
          <FolderKpiCard
            label={t('summary.bookings')}
            value={bookings.count}
            sub={
              summary.hasServices
                ? [
                    t('summary.trips', { count: bookings.trips }),
                    t('bookingsList.hotels', { count: bookings.hotels }),
                    t('bookingsList.experiences', {
                      count: bookings.experiences,
                    }),
                  ].join(' · ')
                : t('bookingsList.emptyTitle')
            }
          />
          <FolderKpiCard
            label={t('summary.travelers')}
            value={summary.travelersCount}
            sub={
              mainGuestName
                ? t('summary.mainGuest', { name: mainGuestName })
                : '--'
            }
          />
          <FolderKpiCard
            label={t('summary.updated')}
            value={
              summary.updatedAt
                ? formatDateTime(summary.updatedAt, 'DD/MM/YYYY hh:mm A')
                : '--'
            }
            sub={t('summary.created', {
              date: folder?.createdAt
                ? formatDate(folder.createdAt, 'DD/MM/YYYY')
                : '--',
              author,
            })}
          />
        </div>
      </div>
      {/* KD-3910: as in production, when the API could not add every price. */}
      {folder?.isTotalPrice === false && (
        <p className="m-0 flex items-center gap-1.5 text-sm text-red-600">
          <i className="fa-light fa-circle-info" aria-hidden="true" />
          {t('bookings.misMatchCurrency')}
        </p>
      )}
    </div>
  );
}
